import React from 'react';
import { ORDER_TYPE_FORECAST, ORDER_TYPE_FUTURES } from '../shop/constants';
import { TranslatedCopy } from '../intl';
import { getTotalAvailability } from './mapAvailability';

export const required = value => (value && /\S/i.test(value) ? undefined : 'Required');

const minLength = min => value => (value && value.length < min ? `Must be ${min} characters or more` : undefined);
const maxLength = max => value => (value && value.length > max ? `Must be ${max} characters or less` : undefined);

const multiplesOf = multiple => value =>
  multiple && value && parseInt(value, 10) % multiple !== 0
    ? `Quantity should be in multiples of ${multiple}`
    : undefined;

const phoneRegex = /^(\d{1,2}[\s-]?)?(\d{3}?[\s-]\d{3}[\s-]\d{4}|(\d{11,12}|\d{10}))$/;
export const phone = value => (value && !phoneRegex.test(value) ? 'Invalid phone number' : undefined);
export const phoneMinLength = minLength(8);
export const phoneMaxLength = maxLength(18);

export const noSelectSpecialChars = value =>
  // CHARS: / * ~ : ` ^ @ # $ % ^ & () {} [] ' " < ? _
  // eslint-disable-next-line
  value && /[@#$%&*_(){}[\]'"<>?*~:`\/^]/.test(value) ? 'Invalid characters' : undefined;

// for Address Title, Name, Name2, c/o, Address 1, Address 2
export const noSpecialCharsAddress = value =>
  // CHARS: / * ~ : ` ^ @ # $ % ^ & () {} [] ' " < ? _ . , - ; \
  // eslint-disable-next-line
  value && /[@#$%&*_(){}\[\]'"<>?*~:`\/^.,\-;\\]/.test(value) ? 'Invalid characters' : undefined;

export const extMaxLength = maxLength(30);
export const ext = value => (value && Number.isNaN(Number(value)) ? 'Invalid extension' : undefined);

export const email = value =>
  value && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,24}$/i.test(value) ? 'Invalid email address' : undefined;
export const notEmail = value => (!email(value) ? 'Should not be an email address' : undefined);
export const emailMatch = (value, allValues) => (allValues.emailAddress === value ? undefined : 'Emails do not match');

export const passwordMatch = (value, allValues) =>
  allValues.newPassword === value ? undefined : 'Passwords do not match';
export const passMinLength = minLength(8);
export const needsUppercase = value =>
  value && !/^(?=.*[A-Z]).+$/.test(value) ? 'Needs an uppercase letter' : undefined;
export const needsLowercase = value =>
  value && !/^(?=.*[a-z]).+$/.test(value) ? 'Needs a lowercase letter' : undefined;
export const needsNumber = value => (value && !/^(?=.*[0-9]).+$/.test(value) ? 'Needs a number' : undefined);
export const needsSpecialChar = value =>
  value && !/^(?=.*[!@#$%^&*(){}[\]/<>?.,:;"'|\\~`\-+=]).+$/.test(value) ? 'Needs a special character' : undefined;
export const noUnderscore = value => (/_/.test(value) ? 'No underscore allowed' : undefined);

/* eslint-disable react/jsx-indent */
// TODO: E-gads, man, the this is so convoluted, the linter doesn't even know what to do with it.
//  Re-implement this (and delete it)
export const noFirstLastName = (firstName, lastName) => value =>
  (value &&
    firstName &&
    // eslint-disable-next-line react/destructuring-assignment
    value
      .toLowerCase()
      .replace(/[^A-Za-z]+/g, '')
      .includes(firstName.toLowerCase().replace(/[^A-Za-z]+/g, ''))) ||
  (value &&
    lastName &&
    // eslint-disable-next-line react/destructuring-assignment
    value
      .toLowerCase()
      .replace(/[^A-Za-z]+/g, '')
      .includes(lastName.toLowerCase().replace(/[^A-Za-z]+/g, ''))) ? (
    <TranslatedCopy id='password.first.last' />
  ) : undefined;
/* eslint-enable */

export const onlyPositiveNumber = value =>
  value && /^([.]\d+|\d+([.]\d+)?)$/.test(value) ? undefined : 'Enter a positive number';

export const hasValueAndIsPositive = value => (value === undefined ? undefined : onlyPositiveNumber(value));
export const isNotZero = value => (+value === 0 ? 'Quantity must be greater than 0.' : undefined);

export const lessThanAvailable = (value, values, props, name) => {
  const { allAvailability, selectedDepartment } = props;
  const isFuturesOrForecastDept = [ORDER_TYPE_FUTURES, ORDER_TYPE_FORECAST].includes(selectedDepartment.type);
  if (!name || !value || !allAvailability || isFuturesOrForecastDept) {
    return undefined;
  }
  const valMinusIndex = name.split('__')[0];
  const splitProductVal = valMinusIndex.match(/(.*)--(.*)/);
  const currSize = splitProductVal[1].split('_').join('.');
  const code = splitProductVal[2];
  const productAvailability = allAvailability && allAvailability[code];
  const sizeAvailability = productAvailability && productAvailability[currSize];
  const available = sizeAvailability && getTotalAvailability(sizeAvailability);
  const totalValue = Object.keys(values).reduce((total, num) => {
    const updatedTotal = total;
    const productCode = num.split('__')[0];

    if (!productCode) return updatedTotal;
    if (!updatedTotal[productCode]) {
      updatedTotal[productCode] = values[num];
    } else {
      updatedTotal[productCode] = parseInt(updatedTotal[productCode], 10) + parseInt(values[num], 10);
    }

    return updatedTotal;
  }, {})[valMinusIndex];

  return value && totalValue && totalValue > available ? 'Quantity exceeds units available.' : undefined;
};

export const maxLength3 = maxLength(3);
export const maxLength4 = maxLength(4);
export const maxLength6 = maxLength(6);
export const maxLength10 = maxLength(10);
export const maxLength18 = maxLength(18);
export const maxLength20 = maxLength(20);
export const maxLength25 = maxLength(25);
export const maxLength35 = maxLength(35);
export const maxLength55 = maxLength(55);
export const maxLength70 = maxLength(70);

export const minLength3 = minLength(3);
export const minLength4 = minLength(4);
export const minLength5 = minLength(5);
export const minLength6 = minLength(6);
export const minLength8 = minLength(8);

export const multiplesOf2 = multiplesOf(2);
export const multiplesOf3 = multiplesOf(3);
export const multiplesOf6 = multiplesOf(6);

export const isProductCode = value => value && /.\w+-\d{3}$/.test(value);
export const isStyleNumber = value => value && /^[A-Z0-9]{1,3}\d{3}$/.test(value);
export const containsProductCode = value => value && /.\w+-\d{3}/.test(value);
export const containsStyleNumber = value => value && /^[A-z0-9]{1,3}\d{3}/.test(value);
export const isColorCode = value => value && /^\d{3}$/.test(value);

export const formatSearchInput = value => {
  if (!containsProductCode(value) && !containsStyleNumber(value)) return value;
  return value
    .replace(/\s*,\s*|\s+/g, ',')
    .split(',')
    .join(',');
};

export const validateTimePeriod = params => {
  const value = params && params.value ? params.value : params;
  return value === 'AM' || value === 'PM' ? undefined : 'Not a valid time period.';
};

export const isNumber = params => {
  const value = params && params.value ? params.value : params;
  return /^\d+$/.test(value) ? undefined : 'Not a number.';
};

export const handleValidatePrePack = selectedProduct => {
  const deliveryUnit =
    selectedProduct &&
    selectedProduct.prePackIndicator &&
    selectedProduct.deliveryUnitOfMeasure === 'EACH' &&
    selectedProduct.deliveryUnit;

  if (!deliveryUnit) return null;
  let validatePrePack;
  switch (deliveryUnit) {
    case 2:
      validatePrePack = multiplesOf2;
      break;
    case 3:
      validatePrePack = multiplesOf3;
      break;
    case 6:
      validatePrePack = multiplesOf6;
      break;
    default:
      validatePrePack = undefined;
  }
  return validatePrePack;
};

export const RULESET_MIN = 'min';
export const RULESET_MAX = 'max';

const minimumVal = (min, total) => value =>
  value && total < min ? `Quantity should be a minimum of ${min}` : undefined;
const maximumVal = (max, total) => value =>
  value && total > max ? `Quantity should be a maximum of ${max}` : undefined;

const MM_POOL = {};
const MINMAX_RULE = (val, total, ruleset) => {
  const id = `${val}_${total}`;
  if (!MM_POOL[id]) {
    MM_POOL[id] = { [RULESET_MIN]: minimumVal(val, total), [RULESET_MAX]: maximumVal(val, total) };
  }
  return MM_POOL[id][ruleset];
};

export const validateMinRules = (min, total) =>
  min ? MINMAX_RULE(parseInt(min, 10), parseInt(total, 10), RULESET_MIN) : undefined;
export const validateMaxRules = (max, total) =>
  max ? MINMAX_RULE(parseInt(max, 10), parseInt(total, 10), RULESET_MAX) : undefined;

export const validateZipCode = zipCode => {
  // zipcode should be XXXXX or XXXXX-XXXX
  const regex = /^(\d{5}(-\d{4})?)$/;
  return regex.test(zipCode) ? undefined : 'Must be XXXXX or XXXXX-XXXX';
};

export const validateJapanZipCode = zipCode => {
  // zipcode should be XXX-XXXX
  const regex = /^[0-9]{3}-[0-9]{4}$/;
  return regex.test(zipCode) ? undefined : 'Must be XXX-XXXX';
};
